// import { TagIcon } from "@heroicons/react/24/outline";
import CustomLoading from "../../../utils/LoadingIndicator/CustomLoading";
import useAdminDashboard from "./useAdminDashboard";

const OffersComponent = () => {
  const { offers, isLoading, errors } = useAdminDashboard(); // Assuming useAdminDashboard provides offers
  if (isLoading.offers) {
    return <CustomLoading />;
  }
  if (errors.offers) {
    return (
      <p className="text-xl font-semibold text-red-700">Some Error Occured</p>
    );
  }
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EGP",
    }).format(amount);
  };

  const offerStats = [
    {
      label: "Total Offers",
      value: offers?.offerUsage?.length || 0,
      color: "text-blue-600",
    },
    {
      label: "Total Coupons",
      value: offers?.couponUsage?.length || 0,
      color: "text-purple-600",
    },
    {
      label: "Total Bundles",
      value: offers?.bundleUsage?.length || 0,
      color: "text-indigo-600",
    },
  ];

  const offerUsage = (offers?.offerUsage || []).slice(0, 3); // Top 3 offers
  const couponUsage = (offers?.couponUsage || []).slice(0, 3); // Top 3 coupons
  const bundleUsage = (offers?.bundleUsage || []).slice(0, 3); // Top 3 bundles

  return (
    <div className="p-3 bg-white rounded-md shadow-sm border border-gray-100">
      <h2 className="text-lg font-semibold text-gray-800 mb-2 leading-tight flex items-center">
        {/* <TagIcon className="w-4 h-4 mr-1 text-gray-600" /> */}
        Offer Analytics
      </h2>

      {/* Offer Stats */}
      <div className="grid grid-cols-1 gap-1 mb-2">
        {offerStats.map((stat, index) => (
          <div key={index} className="text-center p-2 bg-gray-50 rounded-sm">
            <p
              className={`text-base font-semibold ${stat.color} leading-tight`}
            >
              {stat.value}
            </p>
            <p className="text-xs text-gray-600">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Offer Usage */}
      <div className="mb-2">
        <h3 className="text-sm font-semibold text-gray-800 mb-1 leading-tight flex items-center">
          {/* <Star className="w-4 h-4 mr-1 text-yellow-500" /> */}
          Offer Usage
        </h3>
        <div className="space-y-1">
          {offerUsage.length > 0 ? (
            offerUsage.map((offer) => (
              <div
                key={offer._id}
                className="flex items-center justify-between p-2 bg-gray-50 rounded-sm hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xs">
                    {offer.title?.charAt(0) || "O"}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm leading-tight">
                      {offer.title || "Unknown Offer"}
                    </p>
                    <p className="text-xs text-gray-500 truncate max-w-[120px]">
                      {offer._id.slice(-6)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-green-600 text-sm leading-tight">
                    {formatCurrency(offer.totalDiscountGiven)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {offer.usageCount} used
                  </p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-500 text-xs">No offer data available</p>
          )}
        </div>
      </div>

      {/* Coupon Usage */}
      <div className="mb-2">
        <h3 className="text-sm font-semibold text-gray-800 mb-1 leading-tight flex items-center">
          {/* <Star className="w-4 h-4 mr-1 text-yellow-500" /> */}
          Coupon Usage
        </h3>
        <div className="space-y-1">
          {couponUsage.length > 0 ? (
            couponUsage.map((coupon) => (
              <div
                key={coupon._id}
                className="flex items-center justify-between p-2 bg-gray-50 rounded-sm hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xs">
                    {coupon.code?.charAt(0) || "C"}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm leading-tight">
                      {coupon.code || "Unknown Coupon"}
                    </p>
                    <p className="text-xs text-gray-500 truncate max-w-[120px]">
                      {coupon._id.slice(-6)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">
                    {coupon.usedCount} used
                  </p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-500 text-xs">No coupon data available</p>
          )}
        </div>
      </div>

      {/* Bundle Usage */}
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-1 leading-tight flex items-center">
          {/* <Star className="w-4 h-4 mr-1 text-yellow-500" /> */}
          Bundle Usage
        </h3>
        <div className="space-y-1">
          {bundleUsage.length > 0 ? (
            bundleUsage.map((bundle) => (
              <div
                key={bundle._id}
                className="flex items-center justify-between p-2 bg-gray-50 rounded-sm hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xs">
                    {bundle.title?.charAt(0) || "B"}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm leading-tight">
                      {bundle.title || "Unknown Bundle"}
                    </p>
                    <p className="text-xs text-gray-500 truncate max-w-[120px]">
                      {bundle._id.slice(-6)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-green-600 text-sm leading-tight">
                    {formatCurrency(bundle.totalDiscountGiven)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {bundle.usageCount} used
                  </p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-500 text-xs">No bundle data available</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default OffersComponent;
